import React, { useState } from 'react';
import type { MarketGoodsListProps } from './MarketGoodsListProps';
import {
  EmptyView,
  Flex,
  GoodsCard2Column,
  GoodsCardSkeletonList,
  IconEmptyProduct,
} from '@banhala/ui-kit';
import { useAtomValue } from 'jotai';
import { marketGoodsListFilterAtom } from '../../states/GoodsListFilter.atom';
import { useLogDisplayGoods } from '../../../../hooks/useLogDisplayGoods';
import { useAddLikeGoods } from 'shared/src/hooks';
import type { MarketHomeGoodsItem } from 'shared/src/api';
import {
  bridge,
  isAndroid,
  getRangeForEvent,
  isNil,
  isNotNil,
  useNavigator,
} from 'shared/src/utils';
import { useLogMarketDefaultAnalyticsParams } from '../../hooks/useLogMarketAnalyticsEvent';
import { useSyncLocalGoodsLike } from 'shared/src/hooks/useSyncLocalGoodsLike';
import { useMarketHomeGoods } from '../../hooks/useMarketHomeGoods';
import { ErrorBoundary } from 'shared/src/component';
import { usePrefetchMarketHomeGoods } from '../../hooks/usePrefetchMarketHomeGoods';
import { useMarketSno } from '../../hooks/useMarketSno';
import { useRemoveLikeGoods } from 'shared/src/queries';
import type { LogAnalyticsEventParams } from 'shared/src/utils/bridge/logAnalyticsEvent/types';

const MarketGoodsList: React.FC<MarketGoodsListProps> = () => {
  const marketId = useMarketSno();
  const filter = useAtomValue(marketGoodsListFilterAtom);

  const {
    marketHomeGoods: list,
    getNextMarketHomeGoods,
    refetch,
    listId,
    hasNextPage,
    isLoading,
  } = useMarketHomeGoods(marketId, filter[marketId]);

  // const list = useMemo(
  //   () => marketHomeGoods.map(convertCarouselGoodsItemToGoodsData),
  //   [marketHomeGoods],
  // );

  const { logDisplayGoods } = useLogDisplayGoods({
    list_params: undefined,
    list_id: listId,
  });

  const getDefaultMarketAnalyticsParams =
    useLogMarketDefaultAnalyticsParams(marketId);

  const [likeMap, setLikeMap] = useState<{ [key: number]: boolean }>(() =>
    list.reduce((acc, val) => {
      return {
        ...acc,
        [val.item.sno]: isNotNil(val.item.like),
      };
    }, {}),
  );

  const { addLikeGoods } = useAddLikeGoods({
    listParams: undefined,
    onChangeIsLiked: (sno, nextValue) => {
      setLikeMap(prevState => ({ ...prevState, [sno]: nextValue }));
    },
    screen: 'MARKET',
  });

  const { mutateAsync: mutateRemoveLikeGoods } = useRemoveLikeGoods();

  useSyncLocalGoodsLike(setLikeMap);
  const { navigator } = useNavigator();

  if (isLoading) {
    return <MarketGoodsListSkeleton />;
  }

  if (isNil(list)) {
    return null;
  }

  return (
    <GoodsCard2Column<MarketHomeGoodsItem>
      list={list}
      onEndReachedThreshold={2}
      likeMap={likeMap}
      keyExtractor={(item, index) => (item.sno ?? index).toString()}
      ListEmptyComponent={
        <Flex padding={{ top: 114, bottom: 320 }} alignItems={'center'}>
          <EmptyView
            title={
              filter[marketId].keyword === ''
                ? '상품이 존재하지 않습니다'
                : `${filter[marketId].keyword}에 대한 결과가 없습니다`
            }
            icon={<IconEmptyProduct />}
          />
        </Flex>
      }
      onEndReached={getNextMarketHomeGoods}
      onRefresh={refetch}
      showSkeleton={hasNextPage}
      windowSize={isAndroid ? 2 : 10}
      initialNumToRender={4}
      showRanking={list.some(item => isNotNil(item.display.ranking))}
      onPressItem={async (pressed, index) => {
        const { item, render, logging } = pressed;
        bridge.logAnalyticsEvent({
          name: 'CLICK_GOODS',
          analytics: {
            SCREEN: 'MARKET',
            CATEGORY_DEPTH: item.category?.depth,
            CATEGORY_NAME: item.category?.name,
            CATEGORY_SNO: item.category?.sno.toString(),
            DELIVERY_TYPE: item.delivery_type,
            DISCOUNT_RATE: item.discount_rate,
            GOODS_NAME: item.name,
            GOODS_SNO: logging['click'].goods_sno.toString(),
            IMAGE_BADGE_LIST: render.data.image_badge_list.map(
              ({ type }) => type,
            ),
            IS_AD: render.data.has_ad_badge ? '1' : '0',
            IS_NEW: item.is_new ? '1' : '0',
            // item.likes_count가 있으면 item.likes_count를 사용해야한다.
            // 해당 페이지는 item.likes_count가 없어서 null로 처리
            LIKES_COUNT: null,
            // item.likes_count로 500 단위로 끊어서 전송 (2,001~2,500)
            // 해당 페이지는 item.likes_count가 없어서 null로 처리
            LIKES_COUNT_RANGE: null,
            LIST_ID: logging['click'].list_id,
            LIST_INDEX: logging['click'].list_index,
            LIST_PARAMS: undefined,
            MARKET_NAME: item.market.name,
            MARKET_SNO: item.market.sno.toString(),
            MARKET_TYPE: item.market.market_type.name,
            ORIGINAL_PRICE:
              item.first_page_rendering?.original_price ??
              item.linked_option.original_price,
            REVIEW_COUNT: item.total_review_count,
            REVIEW_COUNT_RANGE: getRangeForEvent({
              target: item.total_review_count,
              rangeUnit: 50,
            }),
            REVIEW_RATING: item.positive_review_rate,
            SALES_PRICE: item.price,
            SALES_PRICE_RANGE: getRangeForEvent({
              target: item.price,
              rangeUnit: 5000,
            }),
            STANDARD_CATEGORY_NAME: item.standard_category.name,
            STANDARD_CATEGORY_SNO: item.standard_category.sno.toString(),
            MARKET_TYPE_SNO: item.market.market_type.sno.toString(),

            ...getDefaultMarketAnalyticsParams(),
            INDEX: index,
          },
        });
        navigator.moveToGoods({
          sno: item.sno,
          firstPageRendering: {
            discount_rate: item.first_page_rendering.discount_rate,
            goods_name: item.first_page_rendering.goods_name,
            original_price: item.first_page_rendering.original_price,
            price: item.first_page_rendering.price,
            cover_image: item.first_page_rendering.cover_image,
            market_image: undefined,
            market_name: item.first_page_rendering.market_name ?? undefined,
          },
        });
      }}
      onPressLike={async (pressed, index) => {
        const { item, render, logging } = pressed;

        const analytics: LogAnalyticsEventParams['analytics'] = {
          SCREEN: 'MARKET',
          CATEGORY_DEPTH: item.category?.depth,
          CATEGORY_NAME: item.category?.name,
          CATEGORY_SNO: item.category?.sno.toString(),
          DELIVERY_TYPE: item.delivery_type,
          DISCOUNT_RATE: item.discount_rate,
          GOODS_NAME: item.name,
          GOODS_SNO:
            logging[
              !likeMap[item.sno] ? 'like' : 'unlike'
            ].goods_sno.toString(),
          IMAGE_BADGE_LIST: render.data.image_badge_list.map(
            ({ type }) => type,
          ),
          IS_AD: render.data.has_ad_badge ? '1' : '0',
          IS_NEW: item.is_new ? '1' : '0',
          // item.likes_count가 있으면 item.likes_count를 사용해야한다.
          // 해당 페이지는 item.likes_count가 없어서 null로 처리
          LIKES_COUNT: null,
          // item.likes_count로 500 단위로 끊어서 전송 (2,001~2,500)
          // 해당 페이지는 item.likes_count가 없어서 null로 처리
          LIKES_COUNT_RANGE: null,
          LIST_ID: logging[!likeMap[item.sno] ? 'like' : 'unlike'].list_id,
          LIST_INDEX:
            logging[!likeMap[item.sno] ? 'like' : 'unlike'].list_index,
          // LIST_PARAMS: props.syncedInfiniteRecentlyViewedGoods.list_params,
          MARKET_NAME: item.market.name,
          MARKET_SNO: item.market.sno.toString(),
          MARKET_TYPE: item.market.market_type.name,
          ORIGINAL_PRICE:
            item.first_page_rendering?.original_price ??
            item.linked_option.original_price,
          REVIEW_COUNT: item.total_review_count,
          REVIEW_COUNT_RANGE: getRangeForEvent({
            target: item.total_review_count,
            rangeUnit: 50,
          }),
          REVIEW_RATING: item.positive_review_rate,
          SALES_PRICE: item.price,
          SALES_PRICE_RANGE: getRangeForEvent({
            target: item.price,
            rangeUnit: 5000,
          }),
          STANDARD_CATEGORY_NAME: item.standard_category.name,
          STANDARD_CATEGORY_SNO: item.standard_category.sno.toString(),
          MARKET_TYPE_SNO: item.market.market_type.sno.toString(),
          ...getDefaultMarketAnalyticsParams(),
          INDEX: index,
        };

        if (!likeMap[item.sno]) {
          bridge.logAnalyticsEvent({
            name: 'CLICK_LIKE_GOODS',
            analytics,
          });
          // 좋아요 누르기
          addLikeGoods(item.sno, {
            SCREEN: 'MARKET',
            ...getDefaultMarketAnalyticsParams(),
          });
          return;
        }

        bridge.logAnalyticsEvent({
          name: 'CLICK_UNLIKE_GOODS',
          analytics,
        });

        await mutateRemoveLikeGoods({
          sno: item.sno,
        });

        //해제하기
        bridge.likeGoods({
          goods_sno: item.sno,
          is_liked: false,
        });
        setLikeMap(prevState => ({ ...prevState, [item.sno]: false }));
      }}
      onViewableItemsChanged={logDisplayGoods}
    />
  );
};

const MarketGoodsListSkeleton = () => {
  return (
    <GoodsCardSkeletonList
      count={10}
      numColumns={2}
      bottomContentHeight={124}
    />
  );
};

const MarketGoodsListWrapper = () => {
  const marketId = useMarketSno();
  // Suspense로 인해 market home goods에서 waterfall 발생하는 문제 해결하기 위해 prefetch
  usePrefetchMarketHomeGoods(marketId);

  return (
    <ErrorBoundary>
      <React.Suspense fallback={<MarketGoodsListSkeleton />}>
        {/*<GoodsCardSkeletonList count={10} numColumns={2} />*/}
        <MarketGoodsList />
      </React.Suspense>
    </ErrorBoundary>
  );
};

export default MarketGoodsListWrapper;
